import { useState } from 'react'
import { 
  CheckCircle, 
  XCircle, 
  Award, 
  AlertTriangle, 
  ChevronDown, 
  ChevronUp,
  Shield,
  Calendar,
  ExternalLink
} from 'lucide-react'

interface PositionSkill {
  skill_id: number
  skill_name: string
  category: string
  proficiency_level: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  is_good_to_have?: boolean
}

interface PositionCertification {
  certification_name: string
  certification_body?: string
  is_required: boolean
  expiry_required?: boolean
}

interface CandidateSkill {
  uuid?: string
  skill_id?: number
  skill_name?: string
  skillName?: string
  name?: string
  proficiency_level?: string
  proficiencyLevel?: string
  category?: string
  skillCategory?: string
  isPrimarySkill?: boolean
  yearsOfExperience?: number
  certificationRelated?: boolean
}

interface CompactSkillMatchProps {
  candidateSkills: CandidateSkill[] | any
  positionSkills: PositionSkill[]
  positionCertifications?: PositionCertification[]
  className?: string
  showExpanded?: boolean
  miniMode?: boolean // Ultra compact mode for table cells
  disableClick?: boolean // Disable click to expand behavior
}

const CompactSkillMatch = ({ 
  candidateSkills, 
  positionSkills, 
  positionCertifications = [],
  className = '',
  showExpanded = false,
  miniMode = false,
  disableClick = false
}: CompactSkillMatchProps) => {
  const [isExpanded, setIsExpanded] = useState(showExpanded)

  // Normalize candidate skills to array with robust extraction
  const normalizedCandidateSkills: CandidateSkill[] = (() => {
    if (Array.isArray(candidateSkills)) {
      return candidateSkills
    }
    if (candidateSkills?.skills && Array.isArray(candidateSkills.skills)) {
      return candidateSkills.skills
    }
    if (candidateSkills && typeof candidateSkills === 'object') {
      // Try to extract skills from various possible structures
      const possibleSkills = candidateSkills.skillsData || candidateSkills.candidate_skills || candidateSkills.skills
      if (Array.isArray(possibleSkills)) {
        return possibleSkills
      }
    }
    return []
  })()

  // Helper function to normalize skill names for comparison
  const normalizeSkillName = (skillName: string): string => {
    return skillName?.toLowerCase()
      .trim()
      .replace(/[^\w\s]/g, '') // Remove special characters
      .replace(/\s+/g, ' ') // Normalize whitespace
      || ''
  }

  // Helper function to extract skill name from candidate skill object
  const extractCandidateSkillName = (candSkill: any): string => {
    const skillName = candSkill?.skillName || 
                     candSkill?.skill_name || 
                     candSkill?.name || 
                     candSkill?.title ||
                     (typeof candSkill === 'string' ? candSkill : '')
    return normalizeSkillName(skillName)
  }

  // Helper function to check if two skills match
  const skillsMatch = (positionSkill: PositionSkill, candidateSkill: any): boolean => {
    // First try exact ID match
    if (candidateSkill?.skill_id && positionSkill?.skill_id) {
      if (candidateSkill.skill_id === positionSkill.skill_id) {
        return true
      }
    }

    // Then try name matching with normalization
    const posSkillName = normalizeSkillName(positionSkill.skill_name)
    const candSkillName = extractCandidateSkillName(candidateSkill)
    
    if (posSkillName && candSkillName) {
      // Exact match
      if (posSkillName === candSkillName) {
        return true
      }
      
      // Partial match for compound skills (e.g., "React.js" matches "React")
      if (posSkillName.includes(candSkillName) || candSkillName.includes(posSkillName)) {
        return true
      }
      
      // Handle common variations
      const variations: Record<string, string[]> = {
        'javascript': ['js', 'ecmascript'],
        'typescript': ['ts'],
        'react': ['reactjs', 'react.js'],
        'vue': ['vuejs', 'vue.js'],
        'angular': ['angularjs'],
        'node': ['nodejs', 'node.js'],
        'css': ['css3'],
        'html': ['html5'],
        'aws': ['amazon web services'],
        'gcp': ['google cloud platform', 'google cloud'],
        'k8s': ['kubernetes'],
        'docker': ['containerization'],
        'postgresql': ['postgres'],
        'mongodb': ['mongo']
      }
      
      for (const [key, alts] of Object.entries(variations)) {
        if (posSkillName === key && alts.includes(candSkillName)) return true
        if (candSkillName === key && alts.includes(posSkillName)) return true
      }
    }
    
    return false
  }

  // Calculate skill match analysis
  const analyzeSkills = () => {
    if (!positionSkills || positionSkills.length === 0) {
      return { 
        score: 0, 
        percentage: 0, 
        requiredMatched: [], 
        requiredMissing: [],
        goodToHaveMatched: [],
        goodToHaveMissing: [],
        candidateExtraSkills: []
      }
    }


    const requiredSkills = positionSkills.filter(skill => !skill.is_good_to_have)
    const goodToHaveSkills = positionSkills.filter(skill => skill.is_good_to_have)
    
    let totalPossiblePoints = (requiredSkills.length * 5) + (goodToHaveSkills.length * 1)
    let earnedPoints = 0
    
    const requiredMatched: PositionSkill[] = []
    const requiredMissing: PositionSkill[] = []
    const goodToHaveMatched: PositionSkill[] = []
    const goodToHaveMissing: PositionSkill[] = []

    // Check required skills (5 points each)
    requiredSkills.forEach(posSkill => {
      const hasSkill = normalizedCandidateSkills.some(candSkill => {
        return skillsMatch(posSkill, candSkill)
      })
      
      if (hasSkill) {
        earnedPoints += 5
        requiredMatched.push(posSkill)
      } else {
        requiredMissing.push(posSkill)
      }
    })

    // Check good-to-have skills (1 point each)
    goodToHaveSkills.forEach(posSkill => {
      const hasSkill = normalizedCandidateSkills.some(candSkill => {
        return skillsMatch(posSkill, candSkill)
      })
      
      if (hasSkill) {
        earnedPoints += 1
        goodToHaveMatched.push(posSkill)
      } else {
        goodToHaveMissing.push(posSkill)
      }
    })

    // Find candidate's extra skills (not in position requirements)
    const allPositionSkillNames = positionSkills.map(s => normalizeSkillName(s.skill_name))
    const candidateExtraSkills = normalizedCandidateSkills.filter(candSkill => {
      const skillName = extractCandidateSkillName(candSkill)
      return skillName && !allPositionSkillNames.some(posSkillName => 
        posSkillName === skillName || 
        posSkillName.includes(skillName) || 
        skillName.includes(posSkillName)
      )
    })

    const percentage = totalPossiblePoints > 0 ? Math.round((earnedPoints / totalPossiblePoints) * 100) : 0

    return {
      score: earnedPoints,
      totalPossible: totalPossiblePoints,
      percentage,
      requiredMatched,
      requiredMissing,
      goodToHaveMatched,
      goodToHaveMissing,
      candidateExtraSkills
    }
  }

  const skillAnalysis = analyzeSkills()

  const getScoreColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600 bg-green-50 border-green-200'
    if (percentage >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200'
    return 'text-red-600 bg-red-50 border-red-200'
  }

  const getScoreIcon = (percentage: number) => {
    if (percentage >= 80) return <Award className="w-4 h-4" />
    if (percentage >= 60) return <CheckCircle className="w-4 h-4" />
    return <AlertTriangle className="w-4 h-4" />
  }

  // Mini mode for table cells
  if (miniMode) {
    return (
      <div className={`${className}`}>
        {/* Compact Score Badge */}
        {disableClick ? (
          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border cursor-default ${getScoreColor(skillAnalysis.percentage)}`}>
            {getScoreIcon(skillAnalysis.percentage)}
            <span className="ml-1">{skillAnalysis.percentage}%</span>
          </div>
        ) : (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getScoreColor(skillAnalysis.percentage)} hover:opacity-80 transition-opacity`}
          >
            {getScoreIcon(skillAnalysis.percentage)}
            <span className="ml-1">{skillAnalysis.percentage}%</span>
            {isExpanded ? <ChevronUp className="w-2 h-2 ml-1" /> : <ChevronDown className="w-2 h-2 ml-1" />}
          </button>
        )}
        
        {/* Mini Skills Summary */}
        <div className="mt-1 text-xs text-gray-600 pointer-events-none">
          <div className="flex items-center space-x-2">
            {skillAnalysis.requiredMatched.length > 0 && (
              <span className="text-green-600">✓{skillAnalysis.requiredMatched.length}</span>
            )}
            {skillAnalysis.requiredMissing.length > 0 && (
              <span className="text-red-600">✗{skillAnalysis.requiredMissing.length}</span>
            )}
            {skillAnalysis.goodToHaveMatched.length > 0 && (
              <span className="text-blue-600">+{skillAnalysis.goodToHaveMatched.length}</span>
            )}
            {positionCertifications.length > 0 && (
              <span className="text-orange-600">🛡{positionCertifications.length}</span>
            )}
          </div>
        </div>

        {/* Expanded Details for Mini Mode */}
        {!disableClick && isExpanded && (
          <div className="mt-2 p-2 bg-gray-50 rounded text-xs space-y-2">
            {skillAnalysis.requiredMatched.length > 0 && (
              <div>
                <span className="text-green-700 font-medium">✓ Has: </span>
                <span className="text-gray-700">
                  {skillAnalysis.requiredMatched.map(s => s.skill_name).join(', ')}
                </span>
              </div>
            )}
            {skillAnalysis.requiredMissing.length > 0 && (
              <div>
                <span className="text-red-700 font-medium">✗ Missing: </span>
                <span className="text-gray-700">
                  {skillAnalysis.requiredMissing.map(s => s.skill_name).join(', ')}
                </span>
              </div>
            )}
            {skillAnalysis.goodToHaveMatched.length > 0 && (
              <div>
                <span className="text-blue-700 font-medium">+ Bonus: </span>
                <span className="text-gray-700">
                  {skillAnalysis.goodToHaveMatched.map(s => s.skill_name).join(', ')}
                </span>
              </div>
            )}
            {positionCertifications.length > 0 && (
              <div>
                <span className="text-orange-700 font-medium">🛡 Certs Needed: </span>
                <span className="text-gray-700">
                  {positionCertifications.map(c => c.certification_name).join(', ')}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={`${className}`}>
      {/* Score Badge */}
      <div className="flex items-center space-x-2 mb-2">
        {disableClick ? (
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border cursor-default ${getScoreColor(skillAnalysis.percentage)}`}>
            {getScoreIcon(skillAnalysis.percentage)}
            <span className="ml-1">{skillAnalysis.percentage}% Match</span>
          </div>
        ) : (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getScoreColor(skillAnalysis.percentage)} hover:opacity-80 transition-opacity`}
          >
            {getScoreIcon(skillAnalysis.percentage)}
            <span className="ml-1">{skillAnalysis.percentage}% Match</span>
            {isExpanded ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
          </button>
        )}
      </div>

      {/* Expanded Skills Details */}
      {!disableClick && isExpanded && (
        <div className="space-y-2 text-xs bg-gray-50 p-3 rounded">
          {/* Required Skills */}
          {skillAnalysis.requiredMatched.length > 0 && (
            <div>
              <div className="flex items-center mb-1">
                <CheckCircle className="w-3 h-3 text-green-600 mr-1" />
                <span className="font-medium text-green-700">✓ Has Required ({skillAnalysis.requiredMatched.length}):</span>
              </div>
              <div className="text-gray-700 ml-4">
                {skillAnalysis.requiredMatched.map(skill => skill.skill_name).join(' • ')}
              </div>
            </div>
          )}

          {/* Missing Required Skills */}
          {skillAnalysis.requiredMissing.length > 0 && (
            <div>
              <div className="flex items-center mb-1">
                <XCircle className="w-3 h-3 text-red-600 mr-1" />
                <span className="font-medium text-red-700">✗ Missing Required ({skillAnalysis.requiredMissing.length}):</span>
              </div>
              <div className="text-gray-700 ml-4">
                {skillAnalysis.requiredMissing.map(skill => skill.skill_name).join(' • ')}
              </div>
            </div>
          )}

          {/* Good to Have Skills */}
          {skillAnalysis.goodToHaveMatched.length > 0 && (
            <div>
              <div className="flex items-center mb-1">
                <CheckCircle className="w-3 h-3 text-blue-600 mr-1" />
                <span className="font-medium text-blue-700">+ Good-to-Have ({skillAnalysis.goodToHaveMatched.length}):</span>
              </div>
              <div className="text-gray-700 ml-4">
                {skillAnalysis.goodToHaveMatched.map(skill => skill.skill_name).join(' • ')}
              </div>
            </div>
          )}

          {/* Additional Candidate Skills */}
          {skillAnalysis.candidateExtraSkills.length > 0 && (
            <div>
              <div className="flex items-center mb-1">
                <Award className="w-3 h-3 text-purple-600 mr-1" />
                <span className="font-medium text-purple-700">⭐ Additional Skills ({skillAnalysis.candidateExtraSkills.length}):</span>
              </div>
              <div className="text-gray-700 ml-4">
                {skillAnalysis.candidateExtraSkills.slice(0, 12).map(skill => {
                  return extractCandidateSkillName(skill)
                }).filter(Boolean).join(' • ')}
                {skillAnalysis.candidateExtraSkills.length > 12 && ` • +${skillAnalysis.candidateExtraSkills.length - 12} more`}
              </div>
            </div>
          )}

          {/* Certifications */}
          {positionCertifications.length > 0 && (
            <div>
              <div className="flex items-center mb-1">
                <Shield className="w-3 h-3 text-orange-600 mr-1" />
                <span className="font-medium text-orange-700">🛡️ Required Certifications ({positionCertifications.length}):</span>
              </div>
              <div className="text-gray-700 ml-4 space-y-1">
                {positionCertifications.map((cert, index) => (
                  <div key={index}>
                    {cert.certification_name}
                    {cert.certification_body && <span className="text-gray-500"> ({cert.certification_body})</span>}
                    {cert.is_required && <span className="text-red-600 font-medium"> • Required</span>}
                    {cert.expiry_required && <span className="text-orange-600"> • Expiry tracked</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Score Summary */}
          <div className="border-t pt-2 text-xs text-gray-600">
            <div className="flex justify-between">
              <span>Score: {skillAnalysis.score}/{skillAnalysis.totalPossible} points</span>
              <span>Match: {skillAnalysis.percentage}%</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Required: 5pts each • Good-to-have: 1pt each
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CompactSkillMatch